import { inject, Injectable } from '@angular/core';
import { API_CONFIG, ApiCallingV2Service, DropdownData } from '@zayan-erp/shared';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { LinkedProduct, AttributeValues, Product, ProductAttribute, ProductCreate } from '../models/product.model';
import { ProductSubDetailsSaveData, ModelWithElevations } from '../models/product-sub-details.models';
import { ModelElevationProduct } from '../models/model-elevation-product.model';
import { ProjectModelElevation } from '@zayan-erp/sales';
import { Elevation } from '../models/elevation.models';

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;
  selectedAttributeValues = new BehaviorSubject<any[]>([]);

  createProduct(product: ProductCreate): Observable<Product> {
    return this.#http.postData(`${this.baseUrl}/products`, { showLoader: true, showMessage: true }, product);
  }

  updateProduct(id: string, product: ProductCreate): Observable<Product> {
    return this.#http.putData(`${this.baseUrl}/products/${id}`, product, { showLoader: true, showMessage: true });
  }

  deleteProduct(id: string): Observable<any> {
    return this.#http.deleteData(`${this.baseUrl}/products/${id}`, { showLoader: true, showMessage: true });
  }

  toggleProduct(id: string): Observable<any> {
    return this.#http.patchData(`${this.baseUrl}/products/${id}/toggle`, null, {
      showLoader: true,
      showMessage: false
    });
  }

  readProductById(id: string): Observable<Product> {
    return this.#http.getData(`${this.baseUrl}/products/${id}`, { showLoader: true, showMessage: false });
  }

  readProducts(
    productListId: string,
    categoryId: string,
    subcategoryId: string,
    pageNumber: number,
    pageSize: number,
    searchTerm?: string
  ): Observable<any> {
    const searchTermParam = searchTerm ? `&searchTerm=${searchTerm}` : '';
    return this.#http.getData(
      `${this.baseUrl}/products/search?productListId=${productListId}&categoryId=${categoryId}&subcategoryId=${subcategoryId}&isActive=true&PageSize=${pageSize}&PageNumber=${pageNumber}${searchTermParam}`,
      { showLoader: true, showMessage: false }
    );
  }

  readProductClasses(): Observable<DropdownData[]> {
    return this.#http.getData(`${this.baseUrl}/products/classes`, { showLoader: true, showMessage: false }).pipe(
      map((responseData: any) => {
        return responseData?.map((item: any) => ({
          id: item.id,
          firstLabel: item.name
        })) as DropdownData[];
      })
    );
  }

  readTaxCategories(): Observable<DropdownData[]> {
    return this.#http.getData(`${this.baseUrl}/products/tax-categories`, { showLoader: true, showMessage: false }).pipe(
      map((responseData: any) => {
        return responseData?.map((item: any) => ({
          id: item.id,
          firstLabel: item.description,
          secondLabel: item.code
        })) as DropdownData[];
      })
    );
  }

  readProductAttributes(
    subcategoryId: string
  ): Observable<(Omit<ProductAttribute, 'values'> & { value: AttributeValues })[]> {
    return this.#http.getData(`${this.baseUrl}/products/${subcategoryId}/attributes`, {
      showLoader: false,
      showMessage: false
    });
  }

  assignProductAttributes(productId: string, attributes: any): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/products/${productId}/attributes`,
      { showLoader: true, showMessage: true, message: 'Product attributes assigned successfully' },
      attributes
    );
  }

  saveModelElevationProducts(payload: ProductSubDetailsSaveData): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/modelelevationproducts`,
      { showLoader: true, showMessage: true, message: 'Saved successfully' },
      payload
    );
  }

  readModelElevationProducts(productId: string, projectId: string): Observable<ModelElevationProduct[]> {
    return this.#http.getData(`${this.baseUrl}/modelelevationproducts/product/${productId}/project/${projectId}`, {
      showLoader: true,
      showMessage: false
    });
  }

  updateModelElevationProduct(
    id: string,
    payload: Partial<{
      productTypeId: string;
      quantity: number;
      unitPrice: number;
      retailCredit: number;
      salesDescription: string | null;
      descriptionOverride: boolean;
    }>
  ): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/modelelevationproducts/${id}`, payload, {
      showLoader: true,
      showMessage: true,
      message: 'Updated successfully'
    });
  }

  deleteModelElevationProduct(id: string): Observable<unknown> {
    return this.#http.deleteData(`${this.baseUrl}/modelelevationproducts/${id}`, {
      showLoader: true,
      showMessage: true,
      message: 'Deleted successfully'
    });
  }

  readRegularProducts(): Observable<Product[]> {
    return this.readProducts('', '', '', 1, 1000).pipe(
      map((products: { items: Product[]; totalCount: number }) =>
        products.items.filter((product: Product) => product.productClass.name === 'Regular')
      )
    );
  }

  linkProduct(productId: string, linkedProductId: string, isActive?: boolean): Observable<any> {
    const body = { productId, linkedProductId };
    return this.#http.postData(
      `${this.baseUrl}/products/linked`,
      { showLoader: true, showMessage: true, message: 'Product linked successfully' },
      body
    );
  }

  readLinkedProducts(productId: string): Observable<LinkedProduct[]> {
    return this.#http.getData(`${this.baseUrl}/products/linked/${productId}/products`, {
      showLoader: true,
      showMessage: false
    });
  }

  deleteLinkedProduct(id: string): Observable<any> {
    return this.#http.deleteData(`${this.baseUrl}/products/linked/${id}`, {
      showLoader: true,
      showMessage: true,
      message: 'Linked product deleted successfully'
    });
  }

  readProductLists(): Observable<DropdownData[]> {
    return this.#http
      .getData(`${this.baseUrl}/product-lists?includeInactive=false`, { showLoader: true, showMessage: false })
      .pipe(
        map((responseData: any) => {
          return responseData?.map((item: any) => ({
            id: item.id,
            firstLabel: item.name,
            secondLabel: item.code
          })) as DropdownData[];
        })
      );
  }

  readProductTypes(): Observable<DropdownData[]> {
    return this.#http.getData(`${this.baseUrl}/lookup/product-types`, { showLoader: true, showMessage: false }).pipe(
      map((responseData: any) => {
        const data = responseData ?? [];
        return data.map((item: { id: string; name: string }) => ({
          id: item.id,
          firstLabel: item.name,
          secondLabel: ''
        })) as DropdownData[];
      })
    );
  }

  readModelsByProject(projectId: string): Observable<ModelWithElevations[]> {
    return this.#http
      .getData(`${this.baseUrl}/Project/${projectId}/models/elevations`, { showLoader: true, showMessage: false })
      .pipe(
        map((responseData: any) => {
          if (!responseData || !Array.isArray(responseData)) {
            return [];
          }

          // Group by model
          const modelMap = new Map<string, ModelWithElevations>();

          responseData.forEach((item: ProjectModelElevation) => {
            if (!modelMap.has(item.modelId)) {
              modelMap.set(item.modelId, {
                id: item.modelId,
                projectId: item.projectId,
                modelName: item.modelName,
                elevations: []
              });
            }

            const model = modelMap.get(item.modelId)!;
            model.elevations!.push({
              id: item.elevationId,
              elevationCode: item.elevationCode,
              description: item.elevationCode,
              area: 0,
              modelId: item.modelId
            } as Elevation);
          });

          return Array.from(modelMap.values());
        })
      );
  }
}
